import { FC, useEffect, useRef, useState, useCallback } from 'react'
import { useMemo } from 'react'
import Twitter from 'twitter-text'

import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  CompositeDecorator,
  EditorState,
  ContentBlock,
  convertFromHTML
} from 'draft-js'
import Editor from '@draft-js-plugins/editor'
import createLinkifyPlugin, { extractLinks } from '@draft-js-plugins/linkify'
import createHashtagPlugin from '@draft-js-plugins/hashtag'
import createMentionPlugin, { MentionData } from '@draft-js-plugins/mention'

import {
  ChangeFieldName,
  TAttachmentIds,
  TChangeAttachmentExtrafieldPayload,
  TFieldChangePayload
} from '../../../../store/managePost/newTypes'
import {
  ATTACHMENT_EXTRAFIELD,
  POST_TYPE,
  SOCIAL_NETWORK,
  TExtraFieldsPostSocial
} from '../../../../models/post'

import './UI.scss'
import '@draft-js-plugins/hashtag/lib/plugin.css'
import '@draft-js-plugins/mention/lib/plugin.css'
import '@draft-js-plugins/linkify/lib/plugin.css'
import 'draft-js/dist/Draft.css'
import PluginEditor from '@draft-js-plugins/editor'
import { isFulfilled } from '@reduxjs/toolkit'
import MentionItem from './Mentions/MentionItem'
import { stateToHTML } from 'draft-js-export-html'

const MAX_SYMBOLS_FOR_PREVIEW_TEXT = 150

type TMatchLink = {
  index: number
  lastIndex: number
  raw: string
  schema: string
  text: string
  url: string
}

export type TTransformedMention = MentionData & { errorMessage?: null | string }
export enum MOKUP_MENTION_NAME {
  LOADING = 'loading',
  ERROR = 'error',
  EMPTY = 'empty'
}

function findWithRegex(regex: RegExp, contentBlock: ContentBlock, callback: (start: number, next: number) => any) {
  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
}

const HandleSpan = (props: any) => {
  return (
    <span
      className='mention-plugin__mention'
      data-offset-key={props.offsetKey}
    >
      {props.children}
    </span>
  );
};

// validate length
const validateText = (text: string, social: SOCIAL_NETWORK, maxLength: number | undefined) => {
  switch (social) {
    case SOCIAL_NETWORK.TWITTER_SOCIAL: {
      const tweetterValidation = Twitter.parseTweet(text)
      return tweetterValidation.valid || !text
    }
    default: {
      return maxLength && text.length < maxLength
    }
  }
}

const defineErrorReason = (error: any, social: SOCIAL_NETWORK) => {
  switch (social) {
    case SOCIAL_NETWORK.LINKEDIN_SOCIAL: {
      return 'Company page not found.'
    }
    default:
      return null
  }
}

const mentionFields = {
  [SOCIAL_NETWORK.TWITTER_SOCIAL]: 'username',
  [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: 'vanity_name',
  [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: 'name',
  [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: 'name'
}

const transformDataToSelectOption = (data: any[], social: SOCIAL_NETWORK) => {
  // Fix backend. Remove line after
  if (Array.isArray(data[0]) && !data[0].length) return []

  let result: TTransformedMention[] = data.map((mention) => ({
    mention: mention.mention,
    avatar: mention.logo_url,
    name: mention[mentionFields[social]],
    id: mention.id
  }))
  return result
}

const transformHashToSelectOption = (data: any[], social: SOCIAL_NETWORK) => {
  // Fix backend. Remove line after
  if (Array.isArray(data[0]) && !data[0].length) return []
  let result: TTransformedMention[] = data.map((mention,k) => ({
    id:k+1,
    name:mention ,
  }))
  return result
}



interface MessageFieldProps {
  className?: string
  value: string | null
  editorRaws: string | null | undefined
  social: SOCIAL_NETWORK
  onChange?: (data: TFieldChangePayload) => void
  setExtraField?: (data: TChangeAttachmentExtrafieldPayload) => void
  isHighlights?: boolean
  placeholder?: string
  attachmentIds?: TAttachmentIds
  isEditable?: boolean
  getMentions?: (query: string) => any
  getHashes?: (query:string)=>any
  setMention?: (mention: any) => any
  allowSpaceAtMentions?: boolean
  extraFields?: TExtraFieldsPostSocial
  isExist?: boolean
  postType?: POST_TYPE
  link?: string | null
  maxLength?: number
  isEnabledMentionDropdown?: boolean
  postValue?: any
  maxHashtag?: number | undefined
  setPostValue?: any
}

const MessageField: FC<MessageFieldProps> = ({
  className,
  onChange,
  social,
  placeholder,
  setExtraField,
  attachmentIds,
  getMentions,
  getHashes,
  editorRaws,
  setMention,
  allowSpaceAtMentions = true,
  postType,
  isEditable = false,
  isExist = true,
  link,
  maxLength,
  value,
  postValue,
  maxHashtag,
  setPostValue
}) => {
  const editorRef = useRef<null | PluginEditor>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [seePreviewText, setSeePreviewText] = useState<boolean>(true)

  const contentState = useMemo(() => {
    let contentState
    if (editorRaws) {
      contentState = convertFromRaw(JSON.parse(editorRaws))
    } else {
      contentState = ContentState.createFromText(value || '')
    }
    return contentState as ContentState
  }, [value, editorRaws])
  const [editorState, setState] = useState(() => EditorState.createWithContent(contentState))
  const linkifyPlugin = createLinkifyPlugin()
  const hashtagPlugin = createHashtagPlugin()

  // editor helpers
  const getPlainText = (editorState: EditorState) =>
    editorState.getCurrentContent().getPlainText('\n')
  const getJsonStringifyState = (editorState: EditorState) =>
    JSON.stringify(convertToRaw(editorState.getCurrentContent()))

  // dispatch store
  const setLinkToAttachments = (links: TMatchLink[] | null) => {
    if (links && setExtraField && attachmentIds && !link) {
      setExtraField({
        value: links[0].url,
        field: ATTACHMENT_EXTRAFIELD.LINK,
        ...attachmentIds
      })
    }
  }

  // preview short text
  const isAvailableShowMore = useMemo(
    () =>
      (social === SOCIAL_NETWORK.FACEBOOK_SOCIAL || social === SOCIAL_NETWORK.LINKEDIN_SOCIAL) &&
      value &&
      value.length > MAX_SYMBOLS_FOR_PREVIEW_TEXT,
    [social, value]
  )
  const showPreviewText = () => {
    setSeePreviewText(true)
  }

  const hidePreviewText = () => {
    setSeePreviewText(false)
    editorRef.current?.focus()
  }

  // mention plugin
  const handleStrategy = (contentBlock: ContentBlock, callback: (start: number, next: number) => any, contentState: ContentState) => {
    const HANDLE_REGEX = /(^|[^\w])@([\w\_\.]+)/g
    findWithRegex(HANDLE_REGEX, contentBlock, callback);
  }

  const compositeDecorator = useMemo(() => new CompositeDecorator([
    {
      strategy: handleStrategy,
      component: HandleSpan
    }
  ]), [])
  // set already existed mentions
  const [isOpenMentionSuggestion, setIsOpenMentionSuggestion] = useState(false)
  const [isOpenHashtagSuggestion, setIsOpenHashtagSuggestion] = useState(false)
  const [suggestions, setSuggestions] = useState<TTransformedMention[]>([])
  const [hashes, setHashes] = useState<TTransformedMention[]>([])
  const [isAdded, setIsAdded] = useState<boolean>(true)

  // DEFAULT LIBRARY REGEX + ALLOW DOT

  // const defaultRegExp = '[' + '\\w-' + // Latin-1 Supplement (letters only) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin-1_Supplement
  //   "\xC0-\xD6" + "\xD8-\xF6" + "\xF8-\xFF" + // Latin Extended-A (without deprecated character) - https://en.wikipedia.org/wiki/List_of_Unicode_characters#Latin_Extended-A
  //   "\u0100-\u0148" + "\u014A-\u017F" + // Cyrillic symbols: \u0410-\u044F - https://en.wikipedia.org/wiki/Cyrillic_script_in_Unicode
  //   "\u0410-\u044F" + // hiragana (japanese): \u3040-\u309F - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  //   "\u3040-\u309F" + // katakana (japanese): \u30A0-\u30FF - https://gist.github.com/ryanmcgrath/982242#file-japaneseregex-js
  //   "\u30A0-\u30FF" + // For an advanced explaination about Hangul see https://github.com/draft-js-plugins/draft-js-plugins/pull/480#issuecomment-254055437
  //   "\u3130-\u318F" + "\uAC00-\uD7A3" + // common chinese symbols: \u4e00-\u9eff - http://stackoverflow.com/a/1366113/837709
  //   // extended to \u9fa5 https://github.com/draft-js-plugins/draft-js-plugins/issues/1888
  //   "\u4E00-\u9FA5" + // Arabic https://en.wikipedia.org/wiki/Arabic_(Unicode_block)
  //   "\u0600-\u06FF" + // Vietnamese http://vietunicode.sourceforge.net/charset/
  //   "\xC0-\u1EF9" + '\.]';


  const { MentionSuggestions, mentionPlugin } = useMemo(() => {
    const mentionPlugin:any = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      mentionPrefix: '@',
      mentionTrigger: '@',
      supportWhitespace: allowSpaceAtMentions,
      // mentionRegExp: defaultRegExp,
      mentionComponent: (mentionProps) => {
        return <div className='mention-plugin__mention'>{mentionProps.children}</div>
      }
    })
    const { MentionSuggestions } = mentionPlugin
    return { mentionPlugin, MentionSuggestions }
  }, [])


  const { HashSuggestions, hashSearchPlugin } = useMemo(() => {
    const hashSearchPlugin:any = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      mentionPrefix: '#',
      mentionTrigger:'#',
      supportWhitespace: allowSpaceAtMentions,
      // mentionRegExp: defaultRegExp,
      mentionComponent: (mentionProps) => {
        return <div className='mention-plugin__mention'>{mentionProps.children}</div>
      }
    })
    const { MentionSuggestions: HashSuggestions } = hashSearchPlugin
    return { hashSearchPlugin, HashSuggestions }
  }, [])


  const refTimeoutToGetMentions = useRef<null | NodeJS.Timeout>(null)

  const onOpenChange = (_open: boolean) => {
    setIsOpenMentionSuggestion(_open)
  }
  const onOpenHashChange = (_open: boolean) => {
    setIsOpenHashtagSuggestion(_open)
  }

  useEffect(() => {
    if (!isOpenMentionSuggestion) {
      setSuggestions([])
    }
  }, [isOpenMentionSuggestion])

  useEffect(()=>{
    if(!isOpenHashtagSuggestion){
    setHashes([])
    }
  },[isOpenHashtagSuggestion])

  const onSearchChange = async ({ value }: { value: string }) => {
    return new Promise((resolve) => {
      if (getMentions && value.length > 2) {
        const timeoutToGetTags = setTimeout(async () => {
          setSuggestions([{ name: MOKUP_MENTION_NAME.LOADING, id: null }])
          const getMentionsResponse = await getMentions(value)
          if (isFulfilled(getMentionsResponse)) {
            const transformedMentions = transformDataToSelectOption(getMentionsResponse.payload, social)
            if (!!transformedMentions.length) {
              setSuggestions(transformedMentions)
            } else {
              setSuggestions([{ name: MOKUP_MENTION_NAME.EMPTY, id: null }])
            }
            resolve(transformedMentions)
          } else {
            const errorMessage = defineErrorReason(getMentionsResponse.payload?.error, social)
            setSuggestions([{ name: MOKUP_MENTION_NAME.ERROR, id: null, errorMessage }])
          }
          resolve([])
        }, 500)
        if (refTimeoutToGetMentions.current) clearTimeout(refTimeoutToGetMentions.current)
        refTimeoutToGetMentions.current = timeoutToGetTags
      }
    })
  }

  const onSearchHashChange = async ({ value }: { value: string }) => {
    return new Promise((resolve) => {
      if (getHashes && value.length > 2) {
        const timeoutToGetTags = setTimeout(async () => {
          setHashes([{ name: MOKUP_MENTION_NAME.LOADING, id: null }])
          const getMentionsResponse = await getHashes(value)
          if (isFulfilled(getMentionsResponse)) {
            const transformedMentions = transformHashToSelectOption(getMentionsResponse.payload.tags, social)
            if (!!transformedMentions.length) {
              setHashes(transformedMentions)
            } else {
              setHashes([{ name: MOKUP_MENTION_NAME.EMPTY, id: null }])
            }
            resolve(transformedMentions)
          } else {
            const errorMessage = defineErrorReason(getMentionsResponse.payload?.error, social)
            setHashes([{ name: MOKUP_MENTION_NAME.ERROR, id: null, errorMessage }])
          }
          resolve([])
        }, 500)
        if (refTimeoutToGetMentions.current) clearTimeout(refTimeoutToGetMentions.current)
        refTimeoutToGetMentions.current = timeoutToGetTags
      }
    })
  }

  const onAddMention = (mention: TTransformedMention) => {
    if (setMention) {
      setMention(mention)
    }
  }


  const onAddHash = (mention: any) => {
    if (setHashes) {
      setHashes(hashes)
    }
  }

  // handle editor events
  const handleBlur = (...props: any[]) => {
    const plainText = getPlainText(editorState)

    onChange &&
      onChange({
        socialNetwork: social,
        value: plainText,
        rawValue: getJsonStringifyState(editorState),
        field: ChangeFieldName.MESSAGE
      })

    isAvailableShowMore && setSeePreviewText(true)

    if (postType === POST_TYPE.POST_TYPE_LINK) {
      setLinkToAttachments(extractLinks(plainText))
    }
  }

  const validateHashtag = (message: string, social: string) => {
    let result = false;
    if(social === 'instagram'){
      const maxHashtag = 30;
      const getHashtagCount: number = findHashtags(message);
      if(getHashtagCount > maxHashtag){
        result = true;
      }
    }
    return result;
  }

  const findHashtags = (searchText: string) => {
    var regexp = /\B\#\w\w+\b/g;
    const result = searchText.match(regexp);
    let hashtagCount = 0;
    if (result) {
      hashtagCount = result.length;
    }
    return hashtagCount;
  }

  const handleChange = (editorState: EditorState) => {
    const currentvalue = getPlainText(editorState)
    setState(editorState)

    const isValid = validateText(currentvalue, social, maxLength)
    const isValidate = validateHashtag(currentvalue, social);

    if (!isValid && maxLength) {
      setFieldError(`Message length should be less than ${maxLength} characters.`)
    } else if (isValidate && maxHashtag) {
      setFieldError(`Maximum ${maxHashtag} hashtags only allowed.`)
    } else {
      setFieldError(null)
    }
  }

  function findLinkEntities(contentBlock: any, callback: any, contentState: any) {
    contentBlock.findEntityRanges(
      (character: any) => {
        const entityKey = character.getEntity();
        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === 'LINK'
        );
      },
      callback
    );
  }

  const Link = (props: any) => {
    const {url} = props.contentState.getEntity(props.entityKey).getData();
    return (
      <a target="_blank" onClick={() => window.open(url, "_blank")} style={{color:'#5e93c5', textDecoration: 'none', cursor: 'pointer'}}>
        {props.children}
      </a>
    );
  };

  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Link,
    },
  ]);

  useEffect(() => {
    if(postValue){
      let customValue: any = '';

      const plainText = getPlainText(editorState)
      let currentvalue: any = stateToHTML(editorState.getCurrentContent());
      const match = /\r|\n/.exec(currentvalue);
      const checkspacelast = currentvalue.search(/ [^ ]*$/);
      
      if(postValue.emoji){
        customValue = postValue.emoji;
      } else if(currentvalue !== '' && !postValue.emoji && !match){
        customValue = ' ' + postValue;
      } else if(currentvalue !== '' && !postValue.emoji && match){
        if(checkspacelast === -1){
          customValue = postValue;
        } else {
          customValue =  ' ' + postValue;
        }
      } else {
        customValue = postValue + ' ';
      }

      setPostValue('');

      const result = currentvalue.replace(/(<p[^>]+?>|<p>|<\/p>)/img, "");

      let current_value = result + customValue;
      let finalValue = plainText + customValue;
      const blocksFromHtml = convertFromHTML(current_value);
      const newState = ContentState.createFromBlockArray(blocksFromHtml.contentBlocks, blocksFromHtml.entityMap);
      const newText = EditorState.createWithContent(newState);
      setState(newText);

      onChange &&
      onChange({
        socialNetwork: social,
        value: finalValue,
        rawValue: getJsonStringifyState(newText),
        field: ChangeFieldName.MESSAGE
      })
    }
  }, [postValue]);

  const clickOnPreview = () => {
    setSeePreviewText(false)
    editorRef.current?.focus()
  }

  const handleOnFocus = (...props: any[]) => {
    setSeePreviewText(false)
  }

  // linkifyPlugin, hashtagPlugin, 
  const editorPlugins = []
  const decorators = []

  if (social !== SOCIAL_NETWORK.INSTAGRAM_SOCIAL) {
    editorPlugins.push(mentionPlugin)
    editorPlugins.push(hashSearchPlugin)
  } else {
    // decorators.push(compositeDecorator)
    decorators.push(decorator)
    editorPlugins.push(hashtagPlugin)
  }

  return !isExist || (!isEditable && !value) ? null : (
    <div
      className={`${className}__text content-editable__wrapper ${seePreviewText && isAvailableShowMore && 'show-preview-text'
        }`}
    >
      <div className='editor-wrapper' onClick={clickOnPreview}>
        {seePreviewText && isAvailableShowMore && (
          <div className='editor-wrapper__preview-background'></div>
        )}
        <Editor
          ref={editorRef}
          placeholder={placeholder || 'Type here something...'}
          editorState={editorState}
          onChange={handleChange}
          plugins={editorPlugins}
          decorators={decorators}
          onBlur={handleBlur}
          onFocus={handleOnFocus}
          readOnly={!isEditable}
        />
      </div>
      { social !== SOCIAL_NETWORK.INSTAGRAM_SOCIAL && 
        <>
          <HashSuggestions
            open={isOpenHashtagSuggestion}
            onOpenChange={onOpenHashChange}
            suggestions={hashes}
            onSearchChange={onSearchHashChange}
            onAddMention={onAddHash}
            entryComponent={MentionItem}
          />
          <MentionSuggestions
            open={isOpenMentionSuggestion}
            onOpenChange={onOpenChange}
            suggestions={suggestions}
            onSearchChange={onSearchChange}
            onAddMention={onAddMention}
            entryComponent={MentionItem}
          />
        </>
      }
      {isAvailableShowMore && (
        <div className='see-more' onClick={seePreviewText ? hidePreviewText : showPreviewText}>
          {seePreviewText ? 'See more' : 'Hide more'}
        </div>
      )}
      {fieldError && (
        <div className='validation-error'>
          <div className='validation-error__icon'></div>
          {fieldError}
        </div>
      )}
    </div>
  )
}

export default MessageField
